import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiSend, FiHeart, FiMessageCircle, FiSun, FiMoreVertical } = FiIcons;

const INITIAL_MESSAGES = [
  { id: 1, user: "Sarah Jenkins", text: "Such a beautiful service. Eleanor would have loved the music.", time: "2:04 PM", type: 'chat' },
  { id: 2, user: "David Thompson", text: "Thank you all for joining us today.", time: "2:05 PM", type: 'chat' },
  { id: 3, user: "Admin", text: "Welcome to the Celebration of Life. Please share your favorite memories.", time: "System", type: 'announcement' },
];

const LiveChat = ({ isStreamView = false }) => {
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [inputValue, setInputValue] = useState("");
  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    
    const newMessage = {
      id: Date.now(),
      user: "You",
      text: inputValue,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      type: 'chat'
    };
    
    setMessages([...messages, newMessage]);
    setInputValue("");
  };

  return (
    <div className={`flex flex-col h-full overflow-hidden ${isStreamView ? 'bg-accent-navy' : 'bg-white rounded-3xl border border-brand-dark shadow-xl'}`}>
      <div className={`p-5 flex justify-between items-center border-b ${isStreamView ? 'border-white/10 bg-black/20' : 'border-brand-light bg-brand-light/30'}`}>
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiMessageCircle} className="text-accent-gold w-5 h-5" />
          <span className={`font-bold text-xs uppercase tracking-[0.2em] ${isStreamView ? 'text-white' : 'text-accent-navy'}`}>Sanctuary Chat</span>
        </div>
        <button className={`${isStreamView ? 'text-white/40 hover:text-white' : 'text-text-muted hover:text-accent-navy'}`}>
          <SafeIcon icon={FiMoreVertical} />
        </button>
      </div>

      <div className="flex-grow overflow-y-auto p-5 space-y-6 custom-scrollbar">
        {messages.map((msg) => (
          <motion.div 
            initial={{ opacity: 0, x: 20 }} 
            animate={{ opacity: 1, x: 0 }} 
            key={msg.id} 
            className={`flex flex-col ${msg.type === 'announcement' ? 'items-center' : ''}`}
          >
            {msg.type === 'announcement' ? (
              <div className="bg-white/5 border border-white/10 rounded-full px-4 py-1.5 text-[10px] font-bold text-accent-gold uppercase tracking-widest text-center">
                {msg.text}
              </div>
            ) : (
              <div className="flex gap-3">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-bold text-[10px] flex-shrink-0 ${isStreamView ? 'bg-white/10 text-accent-gold border border-white/10' : 'bg-brand-light text-accent-navy'}`}>
                  {msg.user[0]}
                </div>
                <div className="flex-grow min-w-0">
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className={`text-xs font-bold ${isStreamView ? 'text-white/90' : 'text-accent-navy'}`}>{msg.user}</span>
                    <span className="text-[9px] text-text-muted uppercase font-medium">{msg.time}</span>
                  </div>
                  <div className={`p-3 rounded-2xl rounded-tl-none ${isStreamView ? 'bg-white/5 text-white/70 border border-white/5' : 'bg-brand-light/50 text-text-main'} text-sm font-light leading-relaxed`}>
                    {msg.text}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        ))}
        <div ref={chatEndRef} />
      </div>

      <form onSubmit={handleSendMessage} className={`p-5 border-t ${isStreamView ? 'border-white/10 bg-black/20' : 'border-brand-light bg-white'}`}>
        <div className="flex items-center gap-3">
          <input 
            type="text" 
            placeholder="Share a word of comfort..." 
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            className={`flex-grow px-5 py-3 rounded-2xl text-sm outline-none transition-all ${isStreamView ? 'bg-white/5 border border-white/10 text-white focus:border-accent-gold' : 'bg-brand-light/50 border-none focus:ring-2 focus:ring-accent-gold/20'}`}
          />
          <button 
            type="submit" 
            disabled={!inputValue.trim()}
            className="p-3 bg-accent-gold text-white rounded-xl hover:bg-accent-gold/90 transition-all active:scale-95 disabled:opacity-30 flex-shrink-0 shadow-lg shadow-accent-gold/20"
          >
            <SafeIcon icon={FiSend} className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default LiveChat;